// EID-137: the LOD policy — how much ground detail a view actually needs.
//
// EID-131 gave zarr pyramids a level-addressable interface keyed on RESOLUTION
// (ground units per pixel) rather than zoom, and EID-132 did the same for
// PMTiles via TileMatrixSet metadata. Neither had a consumer: something has to
// look at the camera and say "this frame needs 30 m/px". That is this file.
//
// Three properties are load-bearing:
//
//  1. THE CURRENCY IS GROUND UNITS PER PIXEL, NEVER ZOOM. Only a mercator map
//     view has a zoom, and EIDOS also renders globe, first-person, VR and AR.
//     Zoom appears exactly twice below — once converting a map viewstate INTO
//     a resolution, once converting a resolution INTO a tile level — and both
//     conversions are explicit, latitude-aware and unit-tested.
//
//  2. A SINGLE FRAME DOES NOT HAVE ONE RESOLUTION. A pitched map, a globe and
//     a first-person view all vary across the screen: the foreground of a
//     pitched view legitimately deserves finer data than the horizon, and a
//     globe's limb is edge-on. So the return value is a DISTRIBUTION
//     (`samples`) with scalar summaries, not a single number. Whole-dataset
//     consumers (a zarr pyramid level applies to the entire layer) take
//     `representative`; per-tile consumers walk `samples`.
//
//  3. THE GEOMETRY IS DONE WITH VECTORS, NOT SPHERICAL TRIGONOMETRY. The
//     foreshortening term is `1 / (n̂ · v̂)` for a surface normal and a view
//     direction. That form is hard to get subtly wrong and easy to test at
//     known configurations (nadir → 1, grazing → large).

import { selectScale } from './multiscale';

/** Equatorial circumference of the WGS84 ellipsoid, metres. */
export const EARTH_CIRCUMFERENCE = 40075016.685578488;

/**
 * The sphere radius implied by EARTH_CIRCUMFERENCE, metres (~6378137).
 *
 * Deliberately the spherical-mercator sphere rather than the 6371008.8 mean
 * radius: mercator resolution above is defined on the equatorial
 * circumference, and the globe geometry below has to agree with it or the two
 * view types would disagree about the resolution of the same pixel at the
 * point where deck.gl switches between them. Derived rather than written out
 * so the two constants cannot drift apart.
 */
export const EARTH_RADIUS = EARTH_CIRCUMFERENCE / (2 * Math.PI);

/**
 * Ground resolution of WebMercatorQuad level 0 at the equator, m/px.
 *
 * This is the 256-pixel tile convention that WebMercatorQuad (and every XYZ
 * archive) is defined in: one 256x256 tile spans the world at level 0.
 *
 * deck.gl's viewport zoom uses a 512-pixel world at zoom 0 instead, so a deck
 * zoom of z has the same ground resolution as tile level z+1. That factor of
 * two is the single most common LOD bug in tiled renderers; it is confined to
 * the two conversion helpers below and asserted in the tests, rather than being
 * open-coded at call sites.
 */
export const MERCATOR_LEVEL0_RESOLUTION = EARTH_CIRCUMFERENCE / 256;

/** World size in pixels at deck.gl zoom 0. */
const DECK_WORLD_SIZE = 512;

const DEG = Math.PI / 180;

const clampLatitude = (lat: number) => Math.max(-89.9, Math.min(89.9, lat));

const isFinitePositive = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;

/** One sample of the resolution field across a frame. */
export interface ResolutionSample {
  /**
   * Ground units per pixel at this point. Metres, except for a view whose
   * resolution was requested in degrees (see `toDegrees`).
   */
  resolution: number;
  /** Geographic position of the sample, when the view type defines one. */
  position?: [number, number];
  /** Fraction down the viewport: 0 at the top edge, 1 at the bottom. */
  screenY?: number;
}

/**
 * What a frame needs. `finest` and `coarsest` bracket the distribution;
 * `representative` is the value a whole-dataset consumer should use.
 */
export interface ResolutionDemand {
  /** Smallest (most demanding) ground sample distance in the frame. */
  finest: number;
  /** Largest useful ground sample distance — the horizon or limb end. */
  coarsest: number;
  /**
   * The value to hand a consumer that must pick ONE level for the whole layer,
   * which is every zarr pyramid today: a dataset is opened at a single level.
   *
   * This is `finest`, deliberately. Choosing anything coarser under-resolves
   * the part of the frame the user is most likely looking at, and there is no
   * way to render two levels at once until parent retention lands (EID-138).
   * The cost is over-fetching for a steeply pitched view, where the foreground
   * is a thin sliver — mitigated by the fact that the pyramid level is a
   * step function, so a sliver rarely changes the chosen level. Per-tile
   * consumers should ignore this and walk `samples` instead.
   */
  representative: number;
  /** The distribution itself, ordered near → far where that has meaning. */
  samples: ResolutionSample[];
}

/** The camera, in the shape EIDOS viewstates already carry. */
export interface ViewDescriptor {
  viewType?: string;
  width?: number;
  height?: number;
  /** deck.gl zoom (512px world at zoom 0). Map/globe views. */
  zoom?: number;
  latitude?: number;
  longitude?: number;
  /** Degrees from straight down. 0 = plan view. */
  pitch?: number;
  bearing?: number;
  /** Vertical field of view in degrees. deck.gl's default is 50. */
  fovy?: number;
  /** Camera height above the surface in metres (first-person/VR/AR). */
  altitude?: number;
  /** [lon, lat, altitude] for immersive views. */
  position?: [number, number, number];
}

export interface ResolutionOptions {
  /**
   * The quality dial. 1.0 (default) asks for one data cell per screen pixel.
   *
   * The returned resolution is divided by this, so it behaves the way a user
   * expects a quality slider to behave — higher is sharper:
   *   0.5 → one cell per 2 pixels. One pyramid level coarser, ~4x less data.
   *   2.0 → two cells per pixel. One level finer, ~4x more data.
   * Data volume scales with the SQUARE of this value, which is why the useful
   * range is roughly 0.25–2 rather than arbitrary.
   */
  qualityScale?: number;
  /** How many samples to take across a varying view. Default 8. */
  sampleCount?: number;
}

const DEFAULT_FOVY = 50;
const DEFAULT_SAMPLES = 8;

/**
 * Ground resolution of a plan-view mercator map, m/px at the given latitude.
 * The cos(latitude) term is mercator's scale distortion: at 60°N a pixel covers
 * half the ground it does at the equator.
 */
export const mapResolution = (zoom: number, latitude = 0): number =>
  (EARTH_CIRCUMFERENCE * Math.cos(clampLatitude(latitude) * DEG)) /
  (DECK_WORLD_SIZE * 2 ** zoom);

/**
 * Radians of arc subtended by one pixel, from the vertical field of view.
 * The small-angle form is deliberate: the exact per-pixel angle varies across
 * a rectilinear frame, and pretending otherwise implies a precision the rest
 * of this calculation does not have.
 */
const anglePerPixel = (fovy: number, height: number): number =>
  (fovy * DEG) / Math.max(1, height);

/**
 * Ground resolution at a point seen from a camera, given the surface normal.
 *
 * `distance * anglePerPixel` is the size of a pixel's footprint measured
 * PERPENDICULAR to the view ray. Dividing by the cosine of the incidence angle
 * projects that footprint onto the ground: a surface seen edge-on stretches
 * one pixel across an arbitrarily large patch of ground.
 */
const resolutionAt = (
  distance: number,
  cosIncidence: number,
  radiansPerPixel: number,
): number => {
  // Grazing angles send this to infinity; clamp so a limb pixel reports a very
  // coarse resolution rather than Infinity, which no consumer can compare.
  const cos = Math.max(1e-3, cosIncidence);
  return (distance * radiansPerPixel) / cos;
};

const summarise = (samples: ResolutionSample[]): ResolutionDemand => {
  const values = samples.map((s) => s.resolution).filter(isFinitePositive);
  const finest = values.length ? Math.min(...values) : Number.NaN;
  const coarsest = values.length ? Math.max(...values) : Number.NaN;
  return { finest, coarsest, representative: finest, samples };
};

const applyQuality = (
  demand: ResolutionDemand,
  qualityScale: number,
): ResolutionDemand => {
  if (!isFinitePositive(qualityScale) || qualityScale === 1) return demand;
  const scale = (v: number) => v / qualityScale;
  return {
    finest: scale(demand.finest),
    coarsest: scale(demand.coarsest),
    representative: scale(demand.representative),
    samples: demand.samples.map((s) => ({
      ...s,
      resolution: scale(s.resolution),
    })),
  };
};

/**
 * The screen rows to sample for a camera looking at a plane.
 *
 * Evenly spaced rows PLUS, when it falls inside the frame, the row whose ray
 * points straight down. That row matters because resolution on a plane is
 * monotonic in |ray angle from nadir|, so the frame's finest resolution occurs
 * either at the nadir row or — once the camera is pitched past half the field
 * of view — at the bottom edge. An evenly spaced grid can miss the nadir row
 * entirely (8 samples never land on screenY 0.5), which reports a `finest`
 * that is slightly too coarse and makes the pitched path disagree with the
 * plan path at pitch 0. Including the extremum explicitly is both exact and
 * cheaper than sampling densely enough to stumble on it.
 */
const planarRows = (
  pitchRad: number,
  halfFov: number,
  sampleCount: number,
): number[] => {
  const rows: number[] = [];
  for (let i = 0; i < sampleCount; i++)
    rows.push(1 - i / Math.max(1, sampleCount - 1));
  // rayFromNadir(screenY) = pitchRad + (0.5 - screenY) * 2 * halfFov
  const nadirRow = 0.5 + pitchRad / (2 * halfFov);
  if (nadirRow >= 0 && nadirRow <= 1 && !rows.includes(nadirRow))
    rows.push(nadirRow);
  // Near (bottom, screenY 1) first, so samples[0] is the nearest ground.
  return rows.sort((a, b) => b - a);
};

/**
 * Resolution down a frame for a camera at `cameraHeight` above a plane.
 *
 * Shared by the map and first-person paths: geometrically they are the same
 * problem, differing only in where the camera height comes from (a map derives
 * it from zoom, an immersive view is told it).
 */
const planarDemand = (
  cameraHeight: number,
  radiansPerPixel: number,
  pitchDeg: number,
  fovyDeg: number,
  sampleCount: number,
): ResolutionSample[] => {
  const pitchRad = pitchDeg * DEG;
  const halfFov = (fovyDeg * DEG) / 2;
  const samples: ResolutionSample[] = [];
  for (const screenY of planarRows(pitchRad, halfFov, sampleCount)) {
    const rayFromNadir = pitchRad + (0.5 - screenY) * 2 * halfFov;
    // At or past the horizon the ray never meets the plane.
    if (Math.abs(rayFromNadir) >= Math.PI / 2 - 1e-3) continue;
    const cosIncidence = Math.cos(rayFromNadir);
    const distance = cameraHeight / cosIncidence;
    samples.push({
      resolution: resolutionAt(distance, cosIncidence, radiansPerPixel),
      screenY,
    });
  }
  return samples;
};

/**
 * A plan or pitched map view.
 *
 * Unpitched, this is one number: mercator resolution at the centre latitude.
 * Pitched, the ground recedes and a pixel's footprint grows with the square of
 * distance — the classic result for a camera looking at a plane.
 */
const mapDemand = (
  view: ViewDescriptor,
  sampleCount: number,
): ResolutionDemand => {
  const latitude = view.latitude ?? 0;
  const centre = mapResolution(view.zoom ?? 0, latitude);
  const pitch = view.pitch ?? 0;
  const height = view.height ?? 0;

  if (pitch <= 0 || !height)
    return summarise([{ resolution: centre, screenY: 0.5 }]);

  // Camera height above the ground plane that produces `centre` m/px at the
  // nadir row for this fovy — which is what makes the pitched path agree with
  // the plan path exactly as pitch goes to zero.
  const fovy = view.fovy ?? DEFAULT_FOVY;
  const radiansPerPixel = anglePerPixel(fovy, height);
  const samples = planarDemand(
    centre / radiansPerPixel,
    radiansPerPixel,
    pitch,
    fovy,
    sampleCount,
  );
  // Every row beyond the horizon (near-90° pitch): fall back to the plan value
  // rather than returning an empty distribution.
  if (!samples.length) return summarise([{ resolution: centre, screenY: 0.5 }]);
  return summarise(samples);
};

/**
 * A globe view (`_GlobeView`).
 *
 * The visible disc is not uniform: at the sub-camera point you look straight
 * down, and towards the limb the surface turns edge-on. Sampling along a
 * meridian from the sub-camera point outwards captures that, and is enough for
 * level selection — the azimuthal variation is second-order for a camera
 * looking at the centre of the disc.
 */
const globeDemand = (
  view: ViewDescriptor,
  sampleCount: number,
): ResolutionDemand => {
  const height = view.height ?? 0;
  const radiansPerPixel = anglePerPixel(view.fovy ?? DEFAULT_FOVY, height || 1);
  const latitude = view.latitude ?? 0;
  const longitude = view.longitude ?? 0;

  // Camera altitude: the deck globe zoom is still a mercator-ish scale, so
  // derive the altitude that puts the sub-camera point at the map resolution
  // this zoom implies. That keeps globe and map agreeing where they overlap
  // (deck itself switches to a mercator viewport above zoom 12).
  const subCameraResolution = mapResolution(view.zoom ?? 0, latitude);
  const altitude = subCameraResolution / radiansPerPixel;
  const r = EARTH_RADIUS + altitude;

  // Maximum angular distance from the sub-camera point that is visible at all
  // (the tangent point). Beyond it the surface is over the horizon.
  const horizonAngle = Math.acos(Math.min(1, EARTH_RADIUS / r));

  const samples: ResolutionSample[] = [];
  for (let i = 0; i < sampleCount; i++) {
    const theta = (i / Math.max(1, sampleCount - 1)) * horizonAngle * 0.995;
    // Camera on the +Z axis at radius r; sample point at angle theta from it.
    const px = EARTH_RADIUS * Math.sin(theta);
    const pz = EARTH_RADIUS * Math.cos(theta);
    // Vector from the sample point to the camera.
    const vx = -px;
    const vz = r - pz;
    const distance = Math.hypot(vx, vz);
    // Surface normal at the sample is the outward radial direction.
    const cosIncidence = (px * vx + pz * vz) / (EARTH_RADIUS * distance);
    samples.push({
      resolution: resolutionAt(distance, cosIncidence, radiansPerPixel),
      position: [longitude, clampLatitude(latitude + (theta / DEG) * 0.5)],
    });
  }
  return summarise(samples);
};

/**
 * A first-person, VR or AR view: a camera at eye height looking across a
 * ground plane. Resolution degrades with the SQUARE of ground distance, so the
 * difference between the foreground and fifty metres out is two pyramid levels.
 */
const firstPersonDemand = (
  view: ViewDescriptor,
  sampleCount: number,
): ResolutionDemand => {
  const height = view.height ?? 0;
  const fovy = view.fovy ?? DEFAULT_FOVY;
  const radiansPerPixel = anglePerPixel(fovy, height || 1);
  const eye = isFinitePositive(view.altitude)
    ? view.altitude
    : isFinitePositive(view.position?.[2])
      ? (view.position as [number, number, number])[2]
      : 1.7; // a standing observer, when the spec says nothing
  const samples = planarDemand(
    eye,
    radiansPerPixel,
    view.pitch ?? 0,
    fovy,
    sampleCount,
  );
  if (!samples.length)
    return summarise([{ resolution: eye * radiansPerPixel, screenY: 0.5 }]);
  return summarise(samples);
};

/**
 * Ground units per pixel this view needs, as a distribution.
 *
 * Consumers that pick one level for a whole dataset take `representative`;
 * per-tile traversals walk `samples`.
 */
export const requiredResolution = (
  view: ViewDescriptor,
  options: ResolutionOptions = {},
): ResolutionDemand => {
  const sampleCount = Math.max(1, options.sampleCount ?? DEFAULT_SAMPLES);
  const viewType = view.viewType ?? 'map';
  const demand =
    viewType === 'globe'
      ? globeDemand(view, sampleCount)
      : viewType === 'firstperson' || viewType === 'vr' || viewType === 'ar'
        ? firstPersonDemand(view, sampleCount)
        : mapDemand(view, sampleCount);
  return applyQuality(demand, options.qualityScale ?? 1);
};

// ---------------------------------------------------------------------------
// Unit conversion: a pyramid level's cellSize is in ITS OWN CRS units.
// ---------------------------------------------------------------------------

/** Metres per degree of longitude at a latitude. */
export const metresPerDegree = (latitude = 0): number =>
  (EARTH_CIRCUMFERENCE * Math.cos(clampLatitude(latitude) * DEG)) / 360;

/**
 * Convert a metres-per-pixel demand into the units a level's `cellSize` uses.
 *
 * A GeoZarr level in EPSG:4326 carries degrees; one in a projected CRS carries
 * metres. Comparing a metre demand against a degree cellSize silently selects
 * the coarsest level every time — the numbers differ by a factor of ~10^5 — so
 * this conversion is not optional, and `crs` absent means "assume metres" only
 * because that is what a bare numbered-group store almost always is.
 */
export const resolutionInCrsUnits = (
  metresPerPixel: number,
  crs: string | undefined,
  latitude = 0,
): number =>
  isGeographicCrs(crs)
    ? metresPerPixel / metresPerDegree(latitude)
    : metresPerPixel;

/** EPSG:4326 and friends — a CRS whose axes are degrees. */
export const isGeographicCrs = (crs?: string): boolean => {
  if (!crs) return false;
  const c = crs.toLowerCase();
  return (
    c.includes('4326') ||
    c.includes('crs84') ||
    c.includes('wgs84') ||
    c.includes('ogc:1.3:crs84')
  );
};

// ---------------------------------------------------------------------------
// PMTiles: resolution <-> tile level, by formula rather than metadata lookup.
// ---------------------------------------------------------------------------

/**
 * Ground resolution of one pixel of a 256px tile at an archive level.
 *
 * WebMercatorQuad: metres per pixel, latitude-dependent.
 * WorldCRS84Quad: DEGREES per pixel, latitude-independent — the whole appeal of
 * a geographic quadtree. Archive level z has 2^z columns of 360/2^z degrees
 * (see EID-132's level-numbering note), so a pixel spans 360/(2^z * 256).
 */
export const tileLevelResolution = (
  level: number,
  tileMatrixSet: 'WebMercatorQuad' | 'WorldCRS84Quad' = 'WebMercatorQuad',
  latitude = 0,
): number =>
  tileMatrixSet === 'WorldCRS84Quad'
    ? 360 / (2 ** level * 256)
    : (MERCATOR_LEVEL0_RESOLUTION * Math.cos(clampLatitude(latitude) * DEG)) /
      2 ** level;

/**
 * The coarsest archive level whose pixels are at least as fine as `resolution`.
 *
 * `resolution` must already be in the scheme's units: metres for
 * WebMercatorQuad, degrees for WorldCRS84Quad. Rounding is UP (ceil), so the
 * chosen level always meets or beats the demand rather than sitting one level
 * short of it — under-resolving is visible, over-resolving costs bandwidth that
 * EID-139's budgets exist to cap.
 */
export const resolutionToTileLevel = (
  resolution: number,
  tileMatrixSet: 'WebMercatorQuad' | 'WorldCRS84Quad' = 'WebMercatorQuad',
  latitude = 0,
  bounds?: { minLevel?: number; maxLevel?: number },
): number => {
  const level0 = tileLevelResolution(0, tileMatrixSet, latitude);
  const raw = isFinitePositive(resolution)
    ? Math.ceil(Math.log2(level0 / resolution))
    : 0;
  const min = bounds?.minLevel ?? (tileMatrixSet === 'WorldCRS84Quad' ? 1 : 0);
  const max = bounds?.maxLevel ?? 24;
  return Math.max(min, Math.min(max, Number.isFinite(raw) ? raw : min));
};

// ---------------------------------------------------------------------------
// Disclosure: what is actually on screen.
// ---------------------------------------------------------------------------

export interface EffectiveResolution {
  /** The level's own cell size, in its CRS units. */
  cellSize?: number;
  /** How the level was produced from the source (mean, max, subsample, …). */
  aggregation?: string;
  /** True when a coarser-than-native level is being displayed. */
  downsampled: boolean;
  /** Ratio of displayed cell size to the finest level's, e.g. 8 for 8x. */
  factor?: number;
  /** One-line human summary for a legend. Empty when nothing to disclose. */
  label: string;
}

/**
 * Describe what a chosen level actually shows, for the legend.
 *
 * Scientific honesty, not decoration: showing 8x-downsampled significant wave
 * height without saying so misrepresents the data in a way that a low-poly
 * rock does not. When the level carries an aggregation operator we name it,
 * because "max of 64 cells" and "mean of 64 cells" are different quantities.
 */
export const describeEffectiveResolution = (
  chosen: { level: number; cellSize?: [number, number]; aggregation?: string },
  finest: { level: number; cellSize?: [number, number] } | undefined,
): EffectiveResolution => {
  const cell = chosen.cellSize ? Math.max(...chosen.cellSize) : undefined;
  const finestCell = finest?.cellSize
    ? Math.max(...finest.cellSize)
    : undefined;
  const factor =
    isFinitePositive(cell) && isFinitePositive(finestCell) && finestCell > 0
      ? cell / finestCell
      : undefined;
  const downsampled = factor !== undefined ? factor > 1.001 : false;
  if (!downsampled) {
    return {
      cellSize: cell,
      aggregation: chosen.aggregation,
      downsampled: false,
      label: '',
    };
  }
  // A whole-number factor is the common case — pyramid levels halve — and
  // rendering it as "8.0×" reads as precision the number does not have.
  const rounded =
    factor === undefined
      ? ''
      : Math.abs(factor - Math.round(factor)) < 0.05
        ? String(Math.round(factor))
        : factor.toFixed(1);
  const how = chosen.aggregation ? ` (${chosen.aggregation})` : '';
  return {
    cellSize: cell,
    aggregation: chosen.aggregation,
    downsampled: true,
    factor,
    label: `${rounded}× downsampled${how}`,
  };
};

// ---------------------------------------------------------------------------
// The level-change decision, as a pure function.
// ---------------------------------------------------------------------------

export interface ScaleChangePlan {
  /** The level the camera implies, or null when there is nothing to choose. */
  target: import('./multiscale').ScaleLevel | null;
  /**
   * Whether acting on it means reopening. False when the implied level is
   * already open — the "no refetch" guarantee, decided here rather than
   * trusted to each caller.
   */
  changed: boolean;
  /** The demand converted into the level's own CRS units, for diagnostics. */
  demandInCrsUnits: number;
}

/**
 * Decide which pyramid level a resolution demand implies, and whether that is
 * a change from what is open.
 *
 * Extracted from the provider so the two properties that matter can be tested
 * without a Solid reactive graph or a live store: that a finer demand refines,
 * and that a demand resolving to the open level is inert.
 */
/**
 * How much coarser than the demand a level must be before we drop back to it.
 *
 * EID-138. The dead band that stops a camera parked on a switch boundary from
 * flip-flopping. Refining is allowed immediately — being under-resolved is
 * visible and the user is asking for detail — but coarsening waits until the
 * coarser level is COMFORTABLY sufficient, not merely sufficient.
 *
 * 1.4 is ~sqrt(2), i.e. half a pyramid level: the band covers the jitter a
 * hovering camera produces without ever spanning a whole level, so the chosen
 * level is still never more than one step from the ideal.
 */
const cellOf = (l: { cellSize?: [number, number] }): number | undefined =>
  l.cellSize ? Math.max(...l.cellSize) : undefined;

export const DEFAULT_COARSEN_SLACK = 1.4;

export const planScaleChange = ({
  scales,
  activeScale,
  metresPerPixel,
  latitude = 0,
  coarsenSlack = DEFAULT_COARSEN_SLACK,
}: {
  scales: import('./multiscale').ScaleLevel[] | undefined;
  activeScale?: number;
  metresPerPixel: number;
  latitude?: number;
  /**
   * Hysteresis dial. 1 disables the dead band entirely (every demand takes the
   * ideal level, and a camera on a boundary oscillates). Values below 1 are
   * meaningless and are treated as 1.
   */
  coarsenSlack?: number;
}): ScaleChangePlan => {
  const none = { target: null, changed: false, demandInCrsUnits: Number.NaN };
  if (!scales?.length) return none; // flat datasource: unchanged code path
  const crs = scales[0]?.crs;
  const demandInCrsUnits = resolutionInCrsUnits(metresPerPixel, crs, latitude);
  const ideal = selectScale(scales, demandInCrsUnits);
  if (!ideal) return none;

  // EID-138 hysteresis. Only meaningful once something is already open; the
  // first demand always takes the ideal level.
  const current =
    activeScale === undefined
      ? undefined
      : scales.find((s) => s.level === activeScale);
  let target = ideal;
  if (current && ideal.level < current.level) {
    // Coarsening. The dead band guards only the ADJACENT step: it exists to
    // stop single-level flapping, not to strand a camera several levels finer
    // than it needs. Testing the slack against `ideal` instead would hold a
    // view at level 3 while the ideal was level 0 — permanent over-fetching,
    // which is the opposite of what this issue is for.
    const i = scales.indexOf(current);
    const nextCoarser = i >= 0 ? scales[i + 1] : undefined; // scales run fine -> coarse
    const stepCell = nextCoarser ? cellOf(nextCoarser) : undefined;
    const slack = Math.max(1, coarsenSlack);
    if (!(stepCell !== undefined && demandInCrsUnits >= stepCell * slack)) {
      target = current; // inside the band: hold the current level
    }
  }

  return {
    target,
    changed: target.level !== activeScale,
    demandInCrsUnits,
  };
};

// ---------------------------------------------------------------------------
// EID-144: per-point resolution and the quadtree refinement walk.
//
// A map view can pick one tile level for the whole frame because its screen
// resolution is near-uniform. A globe or a first-person camera cannot: one
// frame legitimately mixes fine tiles near the camera with coarse ones at the
// horizon or the limb. That needs the resolution AT A POINT, not a summary of
// the frame — and then a traversal that subdivides only where it pays.
// ---------------------------------------------------------------------------

/** Great-circle angle between two lon/lat points, radians. */
const angularDistance = (
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number,
): number => {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const dp = (lat2 - lat1) * DEG;
  const dl = (lon2 - lon1) * DEG;
  const a =
    Math.sin(dp / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * Math.asin(Math.min(1, Math.sqrt(a)));
};

/**
 * The camera's ground (nadir) point.
 *
 * deck.gl's `longitude`/`latitude` is the point at the CENTRE OF THE VIEWPORT
 * — what the camera looks at — not where the camera is. Under pitch the camera
 * sits behind that by `height * tan(pitch)`, opposite the bearing. Ignoring
 * that offset would put the finest tiles at the centre of a pitched frame
 * rather than at its bottom edge, which is precisely backwards.
 */
const cameraGroundPoint = (
  view: ViewDescriptor,
  cameraHeight: number,
): [number, number] => {
  const lon = view.longitude ?? 0;
  const lat = view.latitude ?? 0;
  const pitch = (view.pitch ?? 0) * DEG;
  if (!pitch) return [lon, lat];
  const back = cameraHeight * Math.tan(pitch);
  const bearing = (view.bearing ?? 0) * DEG;
  // Move OPPOSITE the bearing by `back` metres.
  const north = -back * Math.cos(bearing);
  const east = -back * Math.sin(bearing);
  const mPerDegLat = EARTH_CIRCUMFERENCE / 360;
  const mPerDegLon = Math.max(1e-6, metresPerDegree(lat));
  return [lon + east / mPerDegLon, clampLatitude(lat + north / mPerDegLat)];
};

/**
 * Ground resolution this view needs AT a geographic point, metres per pixel.
 *
 * Planar views (map, first-person, VR, AR): a camera at height h looking at a
 * point d metres away horizontally needs `rpp * (h² + d²) / h` — the distance
 * term squared, because the slant range grows AND the surface tilts away.
 *
 * Globe: the same 1/(n̂·v̂) foreshortening as globeDemand, with the sample's
 * angular distance from the sub-camera point.
 *
 * Returns Infinity for a point over the horizon — no tile there is worth
 * fetching, which is exactly what the refinement walk should conclude.
 */
export const resolutionAtPoint = (
  view: ViewDescriptor,
  lon: number,
  lat: number,
  options: ResolutionOptions = {},
): number => {
  const viewType = view.viewType ?? 'map';
  const height = view.height ?? 0;
  const fovy = view.fovy ?? DEFAULT_FOVY;
  const radiansPerPixel = anglePerPixel(fovy, height || 1);
  const quality =
    isFinitePositive(options.qualityScale) ? (options.qualityScale as number) : 1;

  if (viewType === 'globe') {
    const subCameraResolution = mapResolution(view.zoom ?? 0, view.latitude ?? 0);
    const altitude = subCameraResolution / radiansPerPixel;
    const r = EARTH_RADIUS + altitude;
    const theta = angularDistance(
      view.longitude ?? 0,
      view.latitude ?? 0,
      lon,
      lat,
    );
    // Past the tangent point the surface is over the horizon.
    if (theta >= Math.acos(Math.min(1, EARTH_RADIUS / r))) return Infinity;
    const px = EARTH_RADIUS * Math.sin(theta);
    const pz = EARTH_RADIUS * Math.cos(theta);
    const vx = -px;
    const vz = r - pz;
    const distance = Math.hypot(vx, vz);
    const cosIncidence = (px * vx + pz * vz) / (EARTH_RADIUS * distance);
    return resolutionAt(distance, cosIncidence, radiansPerPixel) / quality;
  }

  // Planar. Camera height: told to us for an immersive view, derived from
  // zoom for a map.
  const cameraHeight =
    viewType === 'map'
      ? mapResolution(view.zoom ?? 0, view.latitude ?? 0) / radiansPerPixel
      : isFinitePositive(view.altitude)
        ? (view.altitude as number)
        : isFinitePositive(view.position?.[2])
          ? (view.position as [number, number, number])[2]
          : 1.7;

  const [camLon, camLat] = cameraGroundPoint(view, cameraHeight);
  const dLat = (lat - camLat) * (EARTH_CIRCUMFERENCE / 360);
  const dLon = (lon - camLon) * metresPerDegree((camLat + lat) / 2);
  const ground = Math.hypot(dLat, dLon);
  const slant = Math.hypot(ground, cameraHeight);
  // resolution = slant * rpp / cos(incidence), cos(incidence) = h / slant.
  return ((slant * slant) / Math.max(1e-6, cameraHeight)) * radiansPerPixel / quality;
};

export interface TileRefinementOptions {
  /** Tile scheme; supplies gridSize and tileBounds. */
  scheme: {
    minLevel: number;
    gridSize: (z: number) => [number, number];
    tileBounds: (z: number, x: number, y: number) => GeoBoundsTuple;
  };
  /** Visible geographic extent [west, south, east, north]. */
  bounds: GeoBoundsTuple;
  /** Archive data extent; tiles outside are never emitted. */
  extent?: GeoBoundsTuple | null;
  minLevel: number;
  maxLevel: number;
  /** Required ground resolution at a point, in the scheme's units. */
  resolutionAtPoint: (lon: number, lat: number) => number;
  /** Ground resolution one pixel of a tile covers at a level. */
  levelResolution: (level: number, lat: number) => number;
  /**
   * Hard cap on emitted tiles. A refinement walk over a first-person view can
   * otherwise subdivide towards the camera without bound; EID-139 owns real
   * budgets, this is the backstop that keeps a frame finite.
   */
  maxTiles?: number;
}

/** [west, south, east, north] in degrees. */
export type GeoBoundsTuple = [number, number, number, number];

const overlaps = (a: GeoBoundsTuple, b: GeoBoundsTuple) =>
  a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

/**
 * Quadtree screen-space-error traversal: subdivide a tile only while its own
 * pixels are coarser than the view needs THERE.
 *
 * This is the piece a map view does not need and a globe or first-person view
 * cannot work without. Prior art is Cesium's terrain quadtree; the difference
 * here is that the error metric is data resolution rather than geometric
 * height error, so the same walk serves vector tiles.
 *
 * Breadth-first, so a tile budget degrades gracefully — it truncates the
 * FINEST level rather than abandoning a whole region.
 */
export const refineTiles = ({
  scheme,
  bounds,
  extent,
  minLevel,
  maxLevel,
  resolutionAtPoint: required,
  levelResolution,
  maxTiles = 256,
}: TileRefinementOptions): Array<{ x: number; y: number; z: number }> => {
  const start = Math.max(minLevel, scheme.minLevel);
  if (maxLevel < start) return [];

  // Seed with every tile of the coarsest level that meets the viewport.
  let frontier: Array<{ x: number; y: number; z: number }> = [];
  const [cols, rows] = scheme.gridSize(start);
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++) frontier.push({ x, y, z: start });

  const keep = (t: { x: number; y: number; z: number }) => {
    const tb = scheme.tileBounds(t.z, t.x, t.y);
    if (!overlaps(tb, bounds)) return false;
    if (extent && !overlaps(tb, extent)) return false;
    return true;
  };
  frontier = frontier.filter(keep);

  const emitted: Array<{ x: number; y: number; z: number }> = [];
  while (frontier.length) {
    const next: Array<{ x: number; y: number; z: number }> = [];
    for (const tile of frontier) {
      const [w, s, e, n] = scheme.tileBounds(tile.z, tile.x, tile.y);
      // Judge the tile by its MOST DEMANDING point, not its centre.
      //
      // A coarse tile is large: at level 0 one tile covers the world. If its
      // centre alone decides, a tile straddling a high-demand region (the
      // camera's foreground, the sub-camera point of a globe) is judged by a
      // sample that may be nowhere near it, and never refines. Sampling the
      // corners as well as the centre and taking the minimum makes refinement
      // conservative: a tile refines if ANY part of it needs finer data, which
      // is the only safe direction to err in — the alternative silently
      // under-resolves exactly the region the user is looking at.
      const cLat = (s + n) / 2;
      let need = Infinity;
      for (const [plon, plat] of [
        [(w + e) / 2, cLat],
        [w, s],
        [w, n],
        [e, s],
        [e, n],
      ]) {
        const r = required(plon, plat);
        if (r < need) need = r;
      }
      const have = levelResolution(tile.z, cLat);
      // Good enough, at the finest level, or entirely over the horizon
      // (need = Infinity): stop here.
      if (tile.z >= maxLevel || !(have > need)) {
        emitted.push(tile);
        continue;
      }
      for (const child of [
        { x: tile.x * 2, y: tile.y * 2, z: tile.z + 1 },
        { x: tile.x * 2 + 1, y: tile.y * 2, z: tile.z + 1 },
        { x: tile.x * 2, y: tile.y * 2 + 1, z: tile.z + 1 },
        { x: tile.x * 2 + 1, y: tile.y * 2 + 1, z: tile.z + 1 },
      ]) {
        const [childCols, childRows] = scheme.gridSize(child.z);
        if (child.x >= childCols || child.y >= childRows) continue;
        if (keep(child)) next.push(child);
      }
    }
    // Budget: keep what we have rather than expanding another whole level.
    if (emitted.length + next.length > maxTiles) {
      emitted.push(...next.slice(0, Math.max(0, maxTiles - emitted.length)));
      break;
    }
    frontier = next;
  }
  return emitted;
};

/**
 * Convert a metres-per-pixel demand into a tile scheme's own pixel units.
 *
 * WebMercatorQuad measures its pixels in metres, so this is the identity.
 *
 * WorldCRS84Quad measures them in DEGREES, and its tiles are square in degrees
 * — so one pixel spans `d` degrees in both axes. On the ground that is
 * `d * (C/360)` metres north-south and `d * (C/360) * cos(lat)` east-west. The
 * north-south figure is the larger of the two, so it is the binding constraint:
 * satisfy it and the east-west axis is satisfied automatically.
 *
 * Hence NO cosine term here, which is the opposite of the mercator case and
 * easy to get backwards. A cos(lat) division would over-demand at high
 * latitudes — asking for levels a polar archive may not even carry.
 */
export const metresToSchemeUnits = (
  metresPerPixel: number,
  tileMatrixSet: 'WebMercatorQuad' | 'WorldCRS84Quad',
): number =>
  tileMatrixSet === 'WorldCRS84Quad'
    ? metresPerPixel / (EARTH_CIRCUMFERENCE / 360)
    : metresPerPixel;
/**
 * EID-145: what a layer's datasource is actually showing, for the legend.
 *
 * Takes a DataWrapper-shaped object and returns the disclosure, or null when
 * there is nothing to disclose — a flat datasource, a pyramid displaying its
 * finest level, or a level chosen before any cellSize was known.
 *
 * Lives here rather than in the layer manager so the rule is tested as the
 * rule that ships. A UI-local copy would let the test pass while the rendered
 * legend said something else.
 */
export const disclosureFor = (wrapper: {
  multiscale?: { scales?: Array<{ level: number; cellSize?: [number, number] }> };
  scaleLevel?: {
    level: number;
    cellSize?: [number, number];
    aggregation?: string;
    crs?: string;
  };
}): (EffectiveResolution & { units: string }) | null => {
  const scales = wrapper?.multiscale?.scales;
  if (!scales?.length || !wrapper?.scaleLevel) return null;
  const described = describeEffectiveResolution(wrapper.scaleLevel, scales[0]);
  if (!described.downsampled) return null;
  return {
    ...described,
    units: isGeographicCrs(wrapper.scaleLevel.crs) ? '°' : 'm',
  };
};
